import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import {
  NbDialogRef,
  NbCardModule,
  NbButtonModule,
  NbInputModule,
  NbTagModule,
  NbFormFieldModule,
  NbTagComponent,
} from '@nebular/theme';
import { MentionModule, Mentions } from 'angular-mentions';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { KPI } from '../../../@core/api';
import { PerimetersService, UsersService } from '../../../@core/utils';

export interface SendInvestmentEmailResult {
  recipients: string[];
  message: string;
}

@Component({
  selector: 'app-send-investment-email-dialog',
  templateUrl: './send-investment-email-dialog.component.html',
  styleUrls: ['./send-investment-email-dialog.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbInputModule,
    NbTagModule,
    NbFormFieldModule,
    MentionModule,
    TranslateModule,
  ],
})
export class SendInvestmentEmailDialogComponent implements OnInit, OnDestroy {
  public investmentLabel: string;
  public siteId: number;
  public siteName = '';
  public message = '';
  public recipients: string[] = [];
  public inputValue = '';
  public recipientError = '';

  public mentionUsers: KPI.User[] = [];

  public mentionConfig: Mentions = {
    triggerChar: '@',
    maxItems: 10,
    labelKey: 'name',
    dropUp: false,
    mentionFilter(searchString: string, items: KPI.User[]) {
      const needle = searchString.toLowerCase();
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(needle) ||
          item.email.toLowerCase().includes(needle),
      );
    },
    mentionSelect() {
      return '';
    },
  };

  @ViewChild('recipientInput') private recipientInput: ElementRef<HTMLInputElement>;

  private subscriptions: Subscription[] = [];

  constructor(
    private dialogRef: NbDialogRef<SendInvestmentEmailDialogComponent>,
    private usersService: UsersService,
    private perimetersService: PerimetersService,
  ) {}

  public ngOnInit(): void {
    if (!this.siteName && this.siteId != null) {
      this.subscriptions.push(
        this.perimetersService.perimeters$
          .pipe(
            first(),
            map((perimeters) => (perimeters || []).find((p) => p.id === this.siteId)),
          )
          .subscribe((perimeter) => {
            this.siteName = perimeter?.name || '';
          }),
      );
    }

    this.subscriptions.push(
      this.usersService.mentions$
        .pipe(first())
        .subscribe((users: KPI.User[]) => {
          this.mentionUsers = (users || []).filter((user) => !!user.email);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private addRecipient(value: string): boolean {
    const email = (value || '').trim();

    if (!email) {
      this.recipientError = '';
      return true;
    }

    if (Validators.email(new FormControl(email))) {
      this.recipientError = 'Invalid email address';
      return false;
    }

    if (!this.recipients.some((r) => r.toLowerCase() === email.toLowerCase())) {
      this.recipients = [...this.recipients, email];
    }

    this.recipientError = '';
    this.inputValue = '';
    if (this.recipientInput) {
      this.recipientInput.nativeElement.value = '';
    }
    return true;
  }

  public onEnter(event: Event): void {
    event.preventDefault(); // don't submit the dialog form
    this.addRecipient(this.inputValue);
  }

  public onInputChange(): void {
    this.recipientError = '';
  }

  public onTagRemove(tag: NbTagComponent): void {
    this.recipients = this.recipients.filter((r) => r !== tag.text);
  }

  public onMentionSelected(item: KPI.User): void {
    this.addRecipient(item?.email);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public get canSubmit(): boolean {
    return !!this.recipients.length || !!this.inputValue?.trim();
  }

  public submit(): void {
    if (!this.addRecipient(this.inputValue) || !this.recipients.length) {
      return;
    }

    const result: SendInvestmentEmailResult = {
      recipients: this.recipients,
      message: this.message?.trim(),
    };
    this.dialogRef.close(result);
  }
}
